import type { HTMLDynamicListElement } from "@modules/_components/HTMLDynamicListElement"
import { generateBookmarksDropdownList } from "@modules/bookmarks/entries/dropdown-entry-list"
import type { BookmarkEntryData } from "@modules/bookmarks/entries/dropdown-entry-list"
import type { JsonEntryData } from "@modules/bookmarks/entries/entries-api"
import { fetchJsonEntriesFromApi } from "@modules/bookmarks/entries/entries-api"
import { queryElement, requireElement } from "@modules/bookmarks/html-elements"
import type { ApiError } from "@modules/global/ApiError"
import { run } from "@modules/util/service"

import "./bookmarks-dropdown.css"

let dropdownList: HTMLDynamicListElement<BookmarkEntryData> | undefined
let loader: HTMLElement | null
let showLoader = true

function trySetElementTextContent(element: HTMLElement | null, value: string): void {
    if (element === null) {
        return
    }

    element.textContent = value
}

function bookmarksDropdownService() {
    return async (): Promise<void> => {
        const dropdownToggleButton: HTMLAnchorElement = requireElement<HTMLAnchorElement>("#bookmarks-dropdown-button")
        const dropdownContainer: HTMLElement | null = dropdownToggleButton.closest<HTMLLIElement>("li")

        if (!dropdownContainer) {
            return
        }

        dropdownContainer.addEventListener(
            "mouseenter",
            async (): Promise<void> => {
                dropdownList = generateBookmarksDropdownList()
                loader = dropdownList.querySelector(".loader") as HTMLElement | null

                const dropdownAnchor = document.createElement("div")
                dropdownAnchor.classList.add("bookmark-dropdown-anchor")
                dropdownAnchor.addEventListener("transitionend", async (): Promise<void> => {
                    showLoader = true
                })

                dropdownAnchor.appendChild(dropdownList)
                dropdownContainer.appendChild(dropdownAnchor)
            },
            { once: true, passive: true, capture: true },
        )

        dropdownContainer.addEventListener(
            "mouseenter",
            async (): Promise<void> => {
                if (!dropdownList) {
                    return
                }

                const dropdownListElement = dropdownList
                let loaderTimer: NodeJS.Timeout | null = null

                if (showLoader) {
                    loaderTimer = setTimeout(() => {
                        loader?.removeAttribute("hidden")
                        showLoader = false
                    }, 250)
                }

                fetchJsonEntriesFromApi(userData.userId)
                    .then(
                        (entries: JsonEntryData[]): void =>
                            void requestAnimationFrame((): void => {
                                const onlineData: JsonEntryData[] = entries.filter(
                                    (entry: JsonEntryData): boolean => entry.isOnline,
                                )
                                const onlineDataCount: number = onlineData.length
                                const offlineDataCount: number = entries.length - onlineDataCount

                                if (loaderTimer !== null) {
                                    clearTimeout(loaderTimer)
                                }

                                loader?.setAttribute("hidden", "")

                                if (!entries.length) {
                                    queryElement("#bookmarks-with-entries-footer", dropdownListElement)?.setAttribute(
                                        "hidden",
                                        "",
                                    )
                                    queryElement("#bookmarks-no-entries-footer", dropdownListElement)?.removeAttribute(
                                        "hidden",
                                    )
                                } else {
                                    dropdownListElement
                                        .querySelector("#bookmarks-no-entries-footer")
                                        ?.setAttribute("hidden", "")
                                    dropdownListElement
                                        .querySelector("#bookmarks-with-entries-footer")
                                        ?.removeAttribute("hidden")
                                }

                                if (!offlineDataCount) {
                                    queryElement("#bookmarks-offline-count-wrapper", dropdownListElement)?.setAttribute(
                                        "hidden",
                                        "",
                                    )
                                } else {
                                    queryElement(
                                        "#bookmarks-offline-count-wrapper",
                                        dropdownListElement,
                                    )?.removeAttribute("hidden")
                                }

                                trySetElementTextContent(
                                    queryElement("#bookmarks-offline-count", dropdownListElement),
                                    offlineDataCount
                                        .toString()
                                        .concat(offlineDataCount === 1 ? " Eintrag" : " Einträge"),
                                )
                                trySetElementTextContent(
                                    queryElement("#bookmarks-online-count", dropdownListElement),
                                    onlineDataCount.toString().concat(onlineDataCount === 1 ? " Eintrag" : " Einträge"),
                                )

                                dropdownListElement.dataList = onlineData.map<BookmarkEntryData>(
                                    (entry: JsonEntryData): BookmarkEntryData => {
                                        return {
                                            name: entry.name,
                                            locality: entry.locality,
                                            thumbnail: userData.isAgeVerified
                                                ? entry.thumbnailUrlUncensored
                                                : entry.thumbnailUrlPublic,
                                            url: entry.seoUrl,
                                        } as BookmarkEntryData
                                    },
                                )
                            }),
                    )
                    .catch((error: ApiError): void => console.error(error.message))
            },
            { passive: true },
        )
    }
}

if (SiteInfo.bookmarksPageActive) {
    run(bookmarksDropdownService())
}
